<template>
  <main class="pb-20">
    <HeaderMenu>
      <div class="flex gap-3">
        <BaseButton
          class="border-red-500 hover:bg-red-50 font-lato text-sm text-red-500"
          data-cy="district-detail__button--delete"
          @click="toggleDeletePrompt"
        >
          <template #icon-left>
            <JdsIcon
              name="trash"
              size="16px"
              class="text-red-500"
            />
          </template>
          Hapus
        </BaseButton>
        <LinkButton
          :href="`/profil-jawa-barat/tentang/kabupaten-kota/${$route.params.id}/ubah`"
          variant="secondary"
          data-cy="district-detail__button--update"
          class="hover:bg-green-50"
        >
          <template #icon-left>
            <JdsIcon
              name="pencil"
              size="16px"
              class="h-4 text-green-700"
            />
          </template>
          <p class="font-lato font-bold text-sm text-green-700">
            Ubah Data
          </p>
        </LinkButton>
      </div>
    </HeaderMenu>

    <section class="px-6 py-4 rounded-lg bg-white mb-4">
      <h1
        data-cy="district-detail__title"
        class="font-roboto font-medium text-[21px] leading-[34px] text-blue-gray-700 mb-3"
      >
        Detail Kabupaten/Kota
      </h1>

      <div class="flex flex-col gap-4">
        <div class="flex justify-center items-center w-full h-full bg-gray-100 rounded-lg py-8">
          <div
            v-if="loading"
            class="h-28 w-28 rounded-lg animate-pulse bg-gray-200"
          />
          <img
            v-else
            class="h-full w-full object-contain"
            :src="district.logo"
            :style="{ height: '144px', width: 'auto' }"
            alt="logo kabupaten/kota"
            data-cy="district-detail__logo"
          >
        </div>

        <div
          data-cy="district-detail__section-district"
          class="rounded-lg overflow-hidden border border-gray-200"
        >
          <JdsSimpleTable class="!table-auto">
            <thead>
              <tr>
                <th
                  id="about-district"
                  colspan="2"
                  class="!font-roboto !text-sm"
                >
                  Info Detail
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td
                  headers="about-district"
                  class="min-w-[228px] font-lato text-blue-gray-500 font-bold text-sm"
                >
                  Nama Kabupaten/Kota
                </td>
                <td
                  headers="about-district"
                  class="w-full font-lato text-blue-gray-500 text-sm"
                >
                  <div
                    v-if="loading"
                    class="h-4 w-1/4 rounded-lg animate-pulse bg-gray-200"
                  />
                  <div v-else>
                    {{ district.name || '-' }}
                  </div>
                </td>
              </tr>
              <tr>
                <td
                  headers="about-district"
                  class="min-w-[228px] font-lato text-blue-gray-500 font-bold text-sm"
                >
                  Nama Pimpinan
                </td>
                <td
                  headers="about-district"
                  class="w-full font-lato text-blue-gray-500 text-sm"
                >
                  <div
                    v-if="loading"
                    class="h-4 w-1/4 rounded-lg animate-pulse bg-gray-200"
                  />
                  <div v-else>
                    {{ district.chief || '-' }}
                  </div>
                </td>
              </tr>
              <tr>
                <td
                  headers="location-district"
                  class="min-w-[228px] font-lato text-blue-gray-500 font-bold text-sm"
                >
                  Link Website
                </td>
                <td
                  headers="location-district"
                  class="w-full font-lato text-blue-gray-500 text-sm"
                >
                  <div
                    v-if="loading"
                    class="h-4 w-1/5 rounded-lg animate-pulse bg-gray-200"
                  />
                  <div v-else>
                    <template v-if="district.link">
                      <a
                        :href="district.link"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="underline text-blue-gray-500 break-all text-base font-lato font-bold"
                      >
                        {{ district.link }}
                      </a>
                    </template>

                    <template v-else>
                      <span>-</span>
                    </template>
                  </div>
                </td>
              </tr>
              <tr>
                <td
                  headers="about-district"
                  class="min-w-[228px] font-lato text-blue-gray-500 font-bold text-sm"
                >
                  Alamat
                </td>
                <td
                  headers="about-district"
                  class="w-full font-lato text-blue-gray-500 text-sm"
                >
                  <div
                    v-if="loading"
                    class="h-4 w-1/4 rounded-lg animate-pulse bg-gray-200"
                  />
                  <div v-else>
                    {{ district.address || '-' }}
                  </div>
                </td>
              </tr>
              <tr>
                <td
                  headers="about-district"
                  class="min-w-[228px] font-lato text-blue-gray-500 font-bold text-sm"
                >
                  Update Terakhir
                </td>
                <td
                  headers="about-district"
                  class="w-full font-lato text-blue-gray-500 text-sm"
                >
                  <div
                    v-if="loading"
                    class="h-4 w-1/4 rounded-lg animate-pulse bg-gray-200"
                  />
                  <div v-else>
                    {{ lastUpdate }}
                  </div>
                </td>
              </tr>
            </tbody>
          </JdsSimpleTable>
        </div>
      </div>
    </section>

    <!-- Delete Action Prompt -->
    <BaseModal
      :open="isDeletePromptOpen"
      data-cy="district-detail__modal"
      @close="toggleDeletePrompt"
    >
      <div class="w-full h-full">
        <h1 class="font-roboto text-xl leading-8 font-medium text-green-700 mb-6">
          Menghapus Data
        </h1>
        <p class="font-lato text-sm text-gray-800">
          Apakah Anda yakin ingin menghapus Data ini?
        </p>
      </div>
      <template #footer>
        <div class="flex gap-4 justify-end">
          <BaseButton
            class="border-green-700 hover:bg-green-50 text-sm text-green-700"
            data-cy="district-detail__modal-button--cancel"
            @click="toggleDeletePrompt"
          >
            Batal
          </BaseButton>
          <BaseButton
            class="bg-red-500 hover:bg-red-400 text-sm text-white"
            :disabled="deleteLoading"
            data-cy="district-detail__modal-button--delete"
            @click="deleteDistrict(district.id)"
          >
            <p v-if="!deleteLoading">
              Ya, saya yakin
            </p>
            <p
              v-else
              class="flex gap-2 items-center text-gray-500"
            >
              <JdsSpinner
                size="16"
                foreground="#757575"
              />
              Loading...
            </p>
          </BaseButton>
        </div>
      </template>
    </BaseModal>
  </main>
</template>

<script>
import { formatDate } from '@/common/helpers/date';
import { EventBus } from '@/common/helpers/event-bus';
import BaseButton from '@/common/components/BaseButton';
import BaseModal from '@/common/components/BaseModal';
import LinkButton from '@/common/components/LinkButton';
import HeaderMenu from '@/common/components/HeaderMenu';

import { RepositoryFactory } from '@/repositories/RepositoryFactory';

const districtRepository = RepositoryFactory.get('district');

export default {
  name: 'DistrictDetail',
  components: {
    HeaderMenu,
    BaseButton,
    LinkButton,
    BaseModal,
  },
  data() {
    return {
      district: {},
      loading: false,
      deleteLoading: false,
      isPreviewModalOpen: false,
      isDeletePromptOpen: false,
    };
  },
  computed: {
    lastUpdate() {
      if (!this.district.updated_at) return '-';
      return `Terakhir disimpan pada: ${formatDate(this.district.updated_at, 'dd/MM/yyyy - HH:mm')}`;
    },
  },
  async mounted() {
    try {
      this.loading = true;
      const { id } = this.$route.params;
      const response = await districtRepository.getDistrictById(id);
      const { data } = response;

      this.district = data;
    } catch (error) {
      if (error?.response.status === 403) {
        EventBus.$emit('error:forbidden');
      } else {
        this.$toast({
          type: 'error',
          message: 'Gagal mendapatkan data Kabupaten/Kota, silakan coba beberapa saat lagi',
        });
      }
    } finally {
      this.loading = false;
    }
  },
  methods: {
    /**
     * Delete district by id
     * @param {number} id - id of district to delete
     */
    async deleteDistrict(id) {
      try {
        this.deleteLoading = true;
        await districtRepository.deleteDistrictById(id);
        this.$toast({
          type: 'success',
          message: 'Data Kabupaten/Kota telah berhasil dihapus',
        });
      } catch (error) {
        this.$toast({
          type: 'error',
          message: 'Data Kabupaten/Kota gagal dihapus',
        });
      } finally {
        this.deleteLoading = false;
        this.toggleDeletePrompt();
        this.$router.back();
      }
    },

    toggleDeletePrompt() {
      this.isDeletePromptOpen = !this.isDeletePromptOpen;
    },
  },
};
</script>
